import React from 'react'
import PropTypes from 'prop-types'
import { Box, Chip } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'

/**
 * Primary + secondary chip filter bar for the order listing page.
 *
 * Props come from the selector-derived `filterOptions` (aka a
 * `BucketChipBreakdown` for the currently selected bucket, including
 * the aggregated view when the `ALL` bucket is active). Every chip in
 * the backend taxonomy is present in that payload with its per-order
 * count; this component hides any chip whose count is zero so only
 * hold types that actually have orders in the current bucket are
 * rendered.
 */
function HoldFilterBarV2({ filterOptions, selectedHoldFilter, onFilterClick }) {
  const allPrimaryChips = filterOptions?.primaryChips || []
  const allSecondaryChips = filterOptions?.secondaryChips || []

  // "Only show holds that are actually available in the list below" —
  // zero-count chips are suppressed. The backend still emits them for
  // structural stability; the frontend is the one that decides what's
  // worth rendering.
  const primaryChips = allPrimaryChips.filter((chip) => (chip.count || 0) > 0)
  const secondaryChips = allSecondaryChips.filter((chip) => (chip.count || 0) > 0)

  const chipStyle = (code, isActive) => ({
    fontFamily: 'AtlasGroteskRegular, sans-serif',
    fontSize: '0.78rem',
    fontWeight: isActive ? 600 : 400,
    height: 32,
    borderRadius: '16px',
    backgroundColor: isActive ? '#145a4b' : '#ffffff',
    color: isActive ? '#ffffff' : '#333333',
    border: isActive ? 'none' : '1px solid #d0d0d0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: isActive ? '#0f4a3d' : '#f5f5f5',
    },
  })

  const chipLabel = (chip) => {
    if (typeof chip.count === 'number') {
      return `${chip.label} · ${chip.count}`
    }
    return chip.label
  }

  if (primaryChips.length === 0 && secondaryChips.length === 0) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Primary chips row */}
      {primaryChips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {primaryChips.map((chip) => {
            const isActive = selectedHoldFilter === chip.code
            const isInternational = chip.code === 'INT'
            return (
              <Chip
                key={chip.code}
                label={chipLabel(chip)}
                icon={
                  isInternational ? (
                    <LockIcon sx={{ fontSize: 14, color: isActive ? '#fff' : '#666' }} />
                  ) : undefined
                }
                onClick={() => onFilterClick(chip.code)}
                sx={chipStyle(chip.code, isActive)}
              />
            )
          })}
        </Box>
      )}

      {/* Secondary chips row (contextual) */}
      {secondaryChips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {secondaryChips.map((chip) => {
            const isActive = selectedHoldFilter === chip.code
            return (
              <Chip
                key={chip.code}
                label={chipLabel(chip)}
                onClick={() => onFilterClick(chip.code)}
                sx={chipStyle(chip.code, isActive)}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}

HoldFilterBarV2.propTypes = {
  filterOptions: PropTypes.shape({
    orderCount: PropTypes.number,
    primaryChips: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
        holds: PropTypes.arrayOf(
          PropTypes.shape({
            code: PropTypes.string,
            description: PropTypes.string,
            count: PropTypes.number,
          }),
        ),
      }),
    ),
    secondaryChips: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
      }),
    ),
  }),
  selectedHoldFilter: PropTypes.string.isRequired,
  onFilterClick: PropTypes.func.isRequired,
}

export default HoldFilterBarV2
